/**
 * Onboarding v2 (screenshots/100–105, brief §2.18; handoff starter src/onboarding/Onboarding.tsx): the sign-up layout,
 * the Civic Pass keeps filling in, and the last step issues it. State lives in onboardingReducer; the step views are in
 * OnboardingSteps.tsx. Shown to signed-in users who haven't finished it (users.dashboard_onboarded_at) and from Settings
 * ("Change" → /dashboard/welcome, "Edit" address → /welcome/ward).
 *
 * Nothing here grants access (account/logic.ts groupAction):
 *   Resident              not admitted: POST /api/dashboard/request-access (approval only); step 3's picks wait with the
 *                         request and are applied on approval. Admitted residents confirm.
 *   Alderperson's office  POST /api/dashboard/request-access {group:"office", workEmail, ward}: a team-account request to
 *                         the Chicago Politics team. The City email is confirmed with a 6-digit code (Better Auth emailOTP
 *                         email-verification) when it's the account's email; the team's invitation goes there either way.
 *   Lobbyist / advocacy   POST /api/dashboard/request-access {group:"advocacy", org}: join a team that's here, or ask for one.
 * Step 2 (residents) resolves the ward in the browser (wardLookup.ts): the street address never reaches our server.
 * Step 3 saves follows through /api/dashboard/follows and the digest through /api/dashboard/settings.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarPlus, Gavel, House, Landmark, Megaphone, SquareCheck, UserRound } from "lucide-react";
import { groupAction } from "../account/logic";
import type { AgendaMatches, OnboardingStatus } from "../account/types";
import type { UserGroup } from "../nav/navConfig";
import { useFollows } from "../stores/followsStore";
import { toastStore } from "../stores/toastStore";
import { navigate } from "../app/router";
import { duration, easing, prefersReducedMotion, reducedFallback } from "../motion";
import { passView, type Pin } from "../auth/civicPass";
import { LanyardBadge } from "../auth/LanyardBadge";
import { AuthLayout } from "../auth/AuthLayout";
import { authClient } from "../auth/authClient";
import { RESEND_AFTER_S } from "../auth/signupFlow";
import { blocker, initialOnboarding, isCityEmail, onboardingPayload, onboardingReducer, type OnboardingAction, type OnboardingState } from "./onboardingFlow";
import { lookupWard } from "./wardLookup";
import { AdvocacyStep, FollowStep, monthDay, OfficeStep, WardStep, type Office } from "./OnboardingSteps";

const GROUPS: { id: UserGroup; icon: typeof House; title: string; body: string; meta: string }[] = [
  { id: "resident", icon: House, title: "Resident", body: "Your ward, your alderperson, your say.", meta: "Free · just you" },
  { id: "office", icon: Landmark, title: "Alderperson’s office", body: "Ward responses, whip count, meeting log.", meta: "Team · @cityofchicago.org" },
  { id: "advocacy", icon: Megaphone, title: "Lobbyist or advocacy group", body: "Bills, forecasts and coalitions.", meta: "Team · your organization" },
];
const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six"];

const api = (path: string, method: string, body?: unknown) =>
  fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined }).catch(() => null);

/** Where the flow starts: the saved choice, and /welcome/ward or /welcome/follow deep links (Settings). */
function start(status: OnboardingStatus, group: UserGroup | null, team: boolean, path: string): OnboardingState {
  const choice = status.choice ?? group;
  let s = { ...initialOnboarding(), workEmail: status.email ?? "" };
  const run = (a: OnboardingAction) => { s = onboardingReducer(s, a); };
  if (choice) run({ t: "group", group: choice });
  if (team && group && choice === group && group !== "resident") run({ t: "skipWhere", on: true });   // already on their team
  const deep = path.match(/\/welcome\/(ward|follow)/)?.[1];
  if (choice && deep) s = { ...s, step: deep === "follow" ? 3 : s.skipWhere ? 1 : 2 };
  return s;
}

/** Step entrance (tab change recipe: cross-fade + 6 px rise, 200 ms; reduced: 100 ms fade); focus moves to the heading. */
function useStepIn(key: string) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const r = prefersReducedMotion();
    ref.current?.animate(r ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: "translateY(6px)" }, { opacity: 1, transform: "none" }],
      r ? { duration: reducedFallback.duration } : { duration: duration.base, easing: easing.standard });
    ref.current?.querySelector<HTMLElement>("h1")?.focus({ preventScroll: true });
  }, [key]);
  return ref;
}

export function Onboarding({ status, group, admitted, path, orgName = null }: { status: OnboardingStatus; group: UserGroup | null; admitted: boolean; path: string; orgName?: string | null }) {
  const [s, dispatch] = useReducer(onboardingReducer, undefined, () => start(status, group, admitted, path));
  const [found, setFound] = useState<{ photoUrl?: string; neighborhood?: string }>({});
  const [wardError, setWardError] = useState<string | null>(null);
  const [agenda, setAgenda] = useState<AgendaMatches>({ meetingDate: null, items: [] });
  const [offices, setOffices] = useState<Office[]>([]);
  const [preview, setPreview] = useState<UserGroup | null>(null);      // hovering a step-1 option tries that pass on
  const [issuedAt, setIssuedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [codeAt, setCodeAt] = useState<number | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [, tick] = useState(0);
  const follows = useFollows((x) => x.follows);
  const ref = useStepIn(issuedAt ? "issued" : String(s.step));
  const why = blocker(s);
  const codeFlow = s.group === "office" && isCityEmail(status.email ?? "") && s.workEmail.trim().toLowerCase() === (status.email ?? "").toLowerCase();

  useEffect(() => { if (!issuedAt) navigate(s.step === 1 ? "/dashboard/welcome" : `/dashboard/welcome/${s.step === 2 ? "ward" : "follow"}`, { replace: true }); }, [s.step, issuedAt]);
  useEffect(() => { document.title = issuedAt ? "You’re in · Chicago Politics" : `Step ${s.step} of 3 · Chicago Politics`; }, [s.step, issuedAt]);

  // Step 3: items on the next City Council agenda that match the chosen topics.
  useEffect(() => {
    if (s.step !== 3 || !s.topics.length) { setAgenda((a) => ({ ...a, items: [] })); return; }
    const ctl = new AbortController();
    fetch(`/api/dashboard/onboarding/agenda?topics=${encodeURIComponent(s.topics.join(","))}`, { credentials: "include", signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : null)).then((d: AgendaMatches | null) => d && setAgenda(d)).catch(() => {});
    return () => ctl.abort();
  }, [s.step, s.topics]);

  // Step 2, offices: the 50 offices, and the code to the City email (sent once on arrival; Resend after 45 s).
  useEffect(() => {
    if (s.step !== 2 || s.group !== "office") return;
    if (!offices.length) void fetch("/api/pass/offices", { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d: { offices: Office[] } | null) => d && setOffices(d.offices)).catch(() => {});
    if (codeFlow && !s.codeVerified && codeAt === null) void sendCode();
  }, [s.step, s.group]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (codeAt === null) return; const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, [codeAt]);
  const wait = codeAt === null ? 0 : Math.max(0, RESEND_AFTER_S - Math.floor((Date.now() - codeAt) / 1000));

  async function sendCode() {
    setCodeAt(Date.now()); setCodeError(null);
    const r = await authClient.emailOtp.sendVerificationOtp({ email: s.workEmail.trim(), type: "email-verification" }).catch(() => null);
    if (r?.error) setCodeError(r.error.message ?? null);
  }
  const verifyCode = async () => {
    const r = await authClient.emailOtp.verifyEmail({ email: s.workEmail.trim(), otp: code }).catch(() => null);
    if (!r || r.error) { setCodeError(r?.error?.message ?? null); return false; }
    dispatch({ t: "codeVerified", on: true });
    return true;
  };

  const findWard = async () => {
    if (s.ward || s.address.trim().length < 4) return;
    const w = await lookupWard(s.address);
    dispatch({ t: "ward", ward: w && { number: w.number, alderpersonId: w.alderpersonId, alderpersonName: w.alderpersonName } });
    setFound({ photoUrl: w?.photoUrl, neighborhood: w?.neighborhood });
    setWardError(w ? null : "Enter an address in Chicago to find your ward");
  };
  const followed = (recordId: string) => follows.some((f) => f.type === "legislation" && f.refId === recordId);

  /** "Issue my pass": the requests and saves (nothing grants access), then the issued screen. */
  const issue = async () => {
    const p = onboardingPayload(s);
    if (!p.group) return;
    setBusy(true);
    const action = groupAction(p.group, group);
    const titles = new Map(agenda.items.map((i) => [i.recordId, i.title]));
    const want = [
      ...p.topics.filter((l) => !follows.some((f) => f.type === "topic" && f.label === l)).map((l) => ({ type: "topic" as const, label: l, keyword: l })),
      ...p.follow.filter((id) => !followed(id)).map((id) => ({ type: "legislation" as const, label: titles.get(id) ?? id, refId: id })),
      ...(p.group === "resident" && s.ward?.alderpersonId && !follows.some((f) => f.type === "person" && f.refId === s.ward!.alderpersonId)
        ? [{ type: "person" as const, label: `Ald. ${s.ward.alderpersonName}`, refId: s.ward.alderpersonId }] : []),
    ];
    if (action === "request-team" && !s.skipWhere) await api("/api/dashboard/request-access", "POST", { group: p.group, workEmail: p.workEmail, org: p.org, ward: p.group === "office" ? p.ward : undefined });
    if (action === "request-resident" && !admitted) await api("/api/dashboard/request-access", "POST");
    const base = { choice: p.group, ...(p.group === "resident" && p.ward ? { ward: p.ward, wardTotals: p.countInWardTotals } : {}), done: true };
    if (admitted && (action === "confirm" || s.skipWhere)) {
      await Promise.all(want.map((f) => api("/api/dashboard/follows", "POST", { ...f, scope: "personal" })));
      await api("/api/dashboard/settings", "PUT", { digest: p.digest });
      await api("/api/dashboard/onboarding", "PUT", base);
    } else {
      // Waits with the request; a platform admin's approval applies it (dashboard-account.js applyPendingOnboarding).
      await api("/api/dashboard/onboarding", "PUT", { ...base, pending: { follows: want, digest: p.digest } });
    }
    setBusy(false);
    setIssuedAt(new Date().toISOString());
  };

  const next = async () => {
    if (s.step === 3) return void issue();
    if (s.step === 2 && codeFlow && !s.codeVerified) { setBusy(true); const ok = await verifyCode(); setBusy(false); if (!ok) return; }
    dispatch({ t: "next" });
  };

  const view = useMemo(() => {
    const g = preview ?? s.group;
    const office = g === "office" && s.group === "office";
    return passView({
      name: status.name ?? "", email: status.email ?? "", emailVerified: true, group: g, userId: status.userId, issuedAt,
      ward: (g === "resident" || office) && s.ward ? { number: s.ward.number, neighborhood: g === "resident" ? found.neighborhood : undefined, alderpersonName: s.ward.alderpersonName } : null,
      invitedBy: office && s.ward ? `Ward ${s.ward.number} office` : null,
      orgName: g === "advocacy" ? (s.org?.name || (s.skipWhere ? orgName : null)) : null,
      orgStatus: s.skipWhere ? "member" : s.org ? "requested" : null,
      topics: s.topics, follows: s.follow.map((recordId) => ({ recordId })),
    });
  }, [status, s.group, preview, s.ward, s.org, s.topics, s.follow, s.skipWhere, found.neighborhood, issuedAt, orgName]);

  const unpin = (p: Pin) => {
    const a: OnboardingAction = p.kind === "topic" ? { t: "toggleTopic", topic: p.label } : { t: "toggleFollow", recordId: p.label };
    dispatch(a);
    toastStore.getState().show({ message: `Unfollowed ${p.label}`, actionLabel: "Undo", onAction: () => dispatch(a) });
  };
  const openPin = (p: Pin) => { if (p.kind === "bill") window.open(`/dashboard/legislation/${encodeURIComponent(p.label)}`, "_blank", "noopener"); };

  const first = (status.name ?? "").trim().split(/\s+/)[0];
  const when = monthDay(agenda.meetingDate);
  const vote = agenda.items.find((i) => s.follow.includes(i.recordId)) ?? null;
  const cards = issuedAt ? [
    ...(vote ? [{ icon: Gavel, title: `The ${when} vote you follow`, body: vote.title, action: "Open", href: `/dashboard/legislation/${encodeURIComponent(vote.recordId)}` }] : []),
    ...(s.group === "resident" && s.ward ? [{ icon: UserRound, title: "Your alderperson", body: s.ward.alderpersonName, action: "See profile", href: `/dashboard/people/${encodeURIComponent(s.ward.alderpersonId)}` }] : []),
    ...(s.group === "resident" && agenda.items.length ? [{ icon: SquareCheck, title: "How would you vote?", body: `${WORDS[agenda.items.length] ?? agenda.items.length} item${agenda.items.length === 1 ? "" : "s"} on ${when}`, action: "Answer", href: `/dashboard/legislation/${encodeURIComponent(agenda.items[0]!.recordId)}` }] : []),
  ] : [];

  return (
    <AuthLayout className="ob" stage={<LanyardBadge view={view} onUnpin={unpin} onPinOpen={openPin} />}>
      <span className="sr-only" aria-live="polite">{issuedAt ? "" : `Step ${s.step} of 3`}</span>
      <div className="auth__body" ref={ref} key={issuedAt ? "issued" : s.step}>
        {issuedAt ? (<>
          <h1 className="auth__title" tabIndex={-1}>{first ? `You’re in, ${first}.` : "You’re in."}</h1>
          {cards.length > 0 && <ul className="start-here">{cards.map((c) => (
            <li key={c.title}><span className="start-here__icon"><c.icon size={20} aria-hidden /></span><span><b>{c.title}</b><small>{c.body}</small></span>
              <a href={c.href}>{c.action} <ArrowRight size={14} aria-hidden /></a></li>))}</ul>}
          <footer className="auth__nav ob-nav">
            {vote ? <a className="ob-back" href={`/api/dashboard/legislation/${encodeURIComponent(vote.recordId)}/next.ics`}><CalendarPlus size={18} aria-hidden />Add {when} to my calendar</a> : <span />}
            <button className="auth__primary" onClick={() => location.assign("/dashboard")}>Go to my dashboard<ArrowRight size={18} aria-hidden /></button>
          </footer>
        </>) : (<>
          {s.step === 1 && (<>
            <h1 className="auth__title" tabIndex={-1}>Who are you here as?</h1>
            <div role="radiogroup" aria-label="Who are you here as?" className="choice-list" onPointerLeave={() => setPreview(null)}>
              {GROUPS.map((g) => (
                <button key={g.id} type="button" role="radio" aria-checked={s.group === g.id} className={`choice is-${g.id}`} onClick={() => dispatch({ t: "group", group: g.id })}
                  onPointerEnter={() => setPreview(g.id)} onFocus={() => setPreview(g.id)} onBlur={() => setPreview(null)}>
                  <span className="choice__icon"><g.icon size={22} aria-hidden /></span>
                  <span className="choice__text"><b>{g.title}</b><span>{g.body}</span><small>{g.meta}</small></span><span className="radio" aria-hidden />
                </button>))}
            </div>
          </>)}
          {s.step === 2 && s.group === "resident" && <WardStep s={s} dispatch={(a) => { if (a.t === "address") setWardError(null); dispatch(a); }} found={found} error={wardError} onFind={() => void findWard()} />}
          {s.step === 2 && s.group === "office" && <OfficeStep s={s} dispatch={dispatch} offices={offices} code={code} setCode={(c) => { setCode(c); setCodeError(null); }}
            wait={wait} codeSent={codeFlow && !s.codeVerified} codeError={codeError} onResend={() => void sendCode()} />}
          {s.step === 2 && s.group === "advocacy" && <AdvocacyStep s={s} dispatch={dispatch} />}
          {s.step === 3 && <FollowStep s={s} dispatch={dispatch} agenda={agenda} />}
          <footer className="auth__nav ob-nav">
            {s.step > 1 ? <button type="button" className="ob-back" onClick={() => dispatch({ t: "back" })}><ArrowLeft size={18} aria-hidden />Back</button> : <span />}
            <button className="auth__primary" disabled={!!why || busy || (s.step === 2 && codeFlow && !s.codeVerified && code.length !== 6)} aria-describedby={why ? "ob-why" : undefined} onClick={() => void next()}>
              {s.step === 3 ? "Issue my pass" : s.step === 1 && s.group === "resident" ? "Continue as a resident"
                : s.step === 2 && codeFlow && !s.codeVerified ? "Verify and continue" : s.step === 2 && s.group === "advocacy" && s.org?.mode === "join" ? "Request to join" : "Continue"}
              <ArrowRight size={18} aria-hidden /></button>
            {why && <span id="ob-why" className="sr-only">{why}</span>}
          </footer>
        </>)}
      </div>
    </AuthLayout>
  );
}

